import { Badge } from "@aitisal/ui/retail-storefront/badge";
import { Button } from "@aitisal/ui/retail-storefront/button";
import { Card } from "@aitisal/ui/retail-storefront/card";
import {
  ShoppingBag,
  Zap,
} from "lucide-react";
import ResponsiveImage from "../ResponsiveImage";
import { getTitle } from "../../helper/helper";
import { useActiveMenu } from "../../hooks/useActiveMenu";

// Main MidBanner Component
const MidBanner: React.FC = () => {
  const { sections } = useActiveMenu();

  // Find section with "clearance" in the name within the active menu only
  const clearanceSection = sections.find((section) => {
    const sectionName = getTitle(section?.name || "").toLowerCase();
    return sectionName.includes("clearance");
  });

  // Don't render if no clearance section found in active menu
  if (!clearanceSection) {
    return null;
  }

  return (
    <section className="w-full overflow-hidden">
      {/* Hero Banner */}
      <div className="relative w-full py-12 md:py-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-gray-700">
        {/* Background decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-gray-600/10 via-transparent to-gray-600/10" />
          <div className="absolute top-10 left-10 w-64 h-64 bg-gray-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-gray-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left space-y-6">
              {/* Badge */}
              <Badge className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-bold bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-lg shadow-gray-500/30">
                <Zap className="w-3.5 h-3.5" />
                LIMITED TIME ONLY
              </Badge>

              {/* Main Title */}
              <div className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight">
                  <span className="bg-gradient-to-r from-gray-300 via-gray-400 to-gray-200 bg-clip-text text-transparent">
                    {getTitle(clearanceSection?.name || "")}
                  </span>
                </h1>
              </div>

              {/* Description */}
              {clearanceSection?.description && (
                <p className="text-base md:text-lg text-gray-200 max-w-md mx-auto lg:mx-0">
                  {getTitle(clearanceSection.description)}
                </p>
              )}

              {/* CTA Button */}
              <div className="pt-4">
                <Button
                  size="lg"
                  className="group relative overflow-hidden bg-gradient-to-r from-gray-600 to-gray-800 hover:from-gray-700 hover:to-gray-900 text-white font-bold px-8 py-6 text-lg shadow-xl shadow-gray-500/30 hover:shadow-gray-500/50 transition-all duration-300 hover:scale-105"
                  onClick={() => window.location.href = '/c/shop?category=Clearance'}
                >
                  <ShoppingBag className="mr-2 h-5 w-5 group-hover:animate-bounce" />
                  Shop Clearance
                  <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                </Button>
              </div>

            </div>

            {/* Right Content - Featured Product */}
            <div className="flex-1 relative">
              <div className="relative">
                {/* Main featured card */}
                <Card className="group overflow-hidden border-0 shadow-2xl">
                  <div className="relative aspect-square overflow-hidden rounded-2xl">
                    <ResponsiveImage
                      imageObject={clearanceSection?.image}
                      fallbackSrc="https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=1000&h=800&fit=crop"
                      sizes="(min-width: 1024px) 40vw, 90vw"
                      alt="Clearance Sale"
                      className="absolute inset-0 h-full w-full transition-transform duration-500 group-hover:scale-110"
                      style={{ height: "100%" }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                      <Badge className="bg-gray-500 hover:bg-gray-600 text-white mb-2">
                        <Zap className="w-3 h-3 mr-1" />
                        {getTitle(clearanceSection?.name || "")}
                      </Badge>
                      {clearanceSection?.description && (
                        <p className="text-white/80 text-sm">
                          {getTitle(clearanceSection.description)}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>

              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default MidBanner;